import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useContext, useRef, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { userHasPermission } from '../../../authorisation/permission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { UserContext } from '../../../users/UserContext';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { CreateUpdateQuoteCommand, mapQuoteFormModelToCommand } from '../createUpdateQuoteDto';
import { GenerateBillOfMaterialsForQuoteModal } from '../GenerateBillOfMaterialsForQuoteModal';
import { NextStepAfterSavingQuote, QuoteForm } from '../quote-form/QuoteForm';
import { QuoteFormModel, quoteFormModelValidator } from '../quote-form/quoteFormModel';
import { mapQuoteResponseToQuoteFormModel, QuoteResponse } from '../quoteResponse';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ projectId: string; quoteId: string }>;

export const ViewOrEditQuote = ({ project, quoteId }: Props) => {
  const quoteEndpointUrl = `projects/${project.projectId}/quotes/${quoteId}`;

  const getQuoteRequest = useApiRequest<QuoteResponse>({
    endpointUrl: quoteEndpointUrl,
    httpMethod: 'get',
  });

  const updateQuoteRequest = useApiRequest<undefined, CreateUpdateQuoteCommand>({
    endpointUrl: quoteEndpointUrl,
    httpMethod: 'put',
  });

  const userContext = useContext(UserContext);

  const [isGenerateBillOfMaterialsModalOpen, setIsGenerateBillOfMaterialsModalOpen] = useState(false);
  const nextStepAfterSave = useRef<NextStepAfterSavingQuote>('BackToProject');

  const canEdit = (quoteResponse: QuoteResponse) => {
    return !quoteResponse.isSubmitted && userHasPermission({ user: userContext.getUser(), permission: 'Edit' });
  };

  const onSubmit = (formModel: QuoteFormModel, formikHelpers: FormikHelpers<QuoteFormModel>) => {
    const updateQuoteCommand: CreateUpdateQuoteCommand = mapQuoteFormModelToCommand(formModel, project.projectId);

    updateQuoteRequest.sendRequest({
      requestBody: updateQuoteCommand,
      onSuccess: () => {
        formikHelpers.setSubmitting(false);

        if (updateQuoteCommand.quote.isDraft) {
          formikHelpers.setStatus('Saved as draft');
          formikHelpers.setStatus('Ready');
        } else if (nextStepAfterSave.current === 'GenerateBillOfMaterials') {
          setIsGenerateBillOfMaterialsModalOpen(true);
        } else {
          void navigate(projectUrls.projectDetails(project.projectId));
        }
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
        formikHelpers.setStatus('Failed');
      },
    });
  };

  return (
    <GetRequest getDataRequest={getQuoteRequest}>
      {({ response: quoteResponse }) => (
        <>
          <GenerateBillOfMaterialsForQuoteModal
            projectId={project.projectId}
            quoteId={quoteResponse.quoteId}
            isOpen={isGenerateBillOfMaterialsModalOpen}
            closeModal={() => setIsGenerateBillOfMaterialsModalOpen(false)}
            shouldRegenerate={quoteResponse.hasBillOfMaterials}
          />
          <BreadcrumbBar>
            <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbLink to={projectUrls.projectDetails(project.projectId)}>
              {project.projectReference}
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <div>{quoteResponse.name}</div>
            <BreadcrumbSeparator />
            <div>{canEdit(quoteResponse) ? 'Edit Quote' : 'View Quote'}</div>
          </BreadcrumbBar>
          <ProjectHeader project={project} />
          <PageContainer>
            <GetDataForQuoteOrDesignForm project={project}>
              {partsOptions => (
                <>
                  <Header1>{canEdit(quoteResponse) ? 'Edit Quote' : 'View Quote'}</Header1>
                  <Formik<QuoteFormModel>
                    onSubmit={onSubmit}
                    initialValues={mapQuoteResponseToQuoteFormModel(quoteResponse, partsOptions, project.buildTypeCode)}
                    validate={formModel => quoteFormModelValidator.validate(formModel)}
                  >
                    {formProps => (
                      <QuoteForm
                        {...formProps}
                        project={project}
                        dataForQuoteOrDesignForm={partsOptions}
                        disabled={!canEdit(quoteResponse)}
                        setNextStepAfterSave={nextStep => {
                          nextStepAfterSave.current = nextStep;
                        }}
                      />
                    )}
                  </Formik>
                  {updateQuoteRequest.error && <FormErrorBox error={updateQuoteRequest.error} />}
                </>
              )}
            </GetDataForQuoteOrDesignForm>
          </PageContainer>
        </>
      )}
    </GetRequest>
  );
};
